import React, { useContext } from "react";

import { AuthContext } from "./contexts/AuthContext";
import { BrowserRouter as Router, Switch, Route, Redirect } from "react-router-dom";

import PrivateRoute from "./PrivateRoute";
import PublicRoute from "./PublicRoute";

import SignIn from "./views/SignIn";
import Dashboard from "./views/Dashboard";
import ManageHours from "./views/ManageHours";
import SearchHours from "./views/SearchHours";
import Clients from "./views/Clients";
import DigitalOcean from "./views/DigitalOcean";
import Settings from "./views/Settings";
import Users from "./views/Users";
import ManageClients from "./views/ManageClients";
import Inventory from "./views/Inventory";
import BillingReports from "./views/BillingReports";
import EmailCampaigns from "./views/EmailCampaigns";
import CreateEmailCampaign from "./views/CreateEmailCampaign";
import CampaignDetails from "./views/CampaignDetails";
import ManageUnsubscribes from "./views/ManageUnsubscribes";
import ManageRecipientLists from "./views/ManageRecipientLists";
import DataPulse from "./views/DataPulse";
import CreateSurvey from "./views/CreateSurvey";
import ViewSurvey from "./views/ViewSurvey";
import SurveyAnalytics from "./views/SurveyAnalytics";
import SystemHealth from "./views/SystemHealth";
import MaintenanceTasks from "./views/MaintenanceTasks";
import MaintenanceJobs from "./views/MaintenanceJobs";
import MaintenanceSchedules from "./views/MaintenanceSchedules";
import ManageAssets from "./components/ManageAssets";

import CircularIndeterminate from "./views/CircularIndeterminate";

const Routes = () => {
  const { isLoading, isVerified, user } = useContext(AuthContext);


  return (
    <Router>
      <Switch>
        <PublicRoute path="/signin" component={SignIn} exact />

        <PrivateRoute path="/" exact>
          {isLoading || !isVerified ? <CircularIndeterminate /> : <Dashboard />}
        </PrivateRoute>

        <PrivateRoute path="/dashboard">
          {isLoading || !isVerified ? <CircularIndeterminate /> : <Dashboard />}
        </PrivateRoute>

        <PrivateRoute path="/manage-hours">
          {isLoading || !isVerified ? (
            <CircularIndeterminate />
          ) : (
            <ManageHours />
          )}
        </PrivateRoute>

        <PrivateRoute path="/search-hours">
          {isLoading || !isVerified ? (
            <CircularIndeterminate />
          ) : (
            <SearchHours />
          )}
        </PrivateRoute>

        <PrivateRoute path="/clients">
          {isLoading || !isVerified ? <CircularIndeterminate /> : <Clients />}
        </PrivateRoute>

        <PrivateRoute path="/digitalocean">
          {isLoading || !isVerified ? (
            <CircularIndeterminate />
          ) : user?.type === 'super_admin' ? (
            <DigitalOcean />
          ) : (
            <Redirect to="/dashboard" />
          )}
        </PrivateRoute>

        <PrivateRoute path="/my-settings">
          {isLoading || !isVerified ? <CircularIndeterminate /> : <Settings />}
        </PrivateRoute>

        <PrivateRoute path="/users">
          {isLoading || !isVerified ? <CircularIndeterminate /> : <Users />}
        </PrivateRoute>

        <PrivateRoute path="/manage-clients">
          {isLoading || !isVerified ? <CircularIndeterminate /> : <ManageClients />}
        </PrivateRoute>

        <PrivateRoute path="/inventory">
          {isLoading || !isVerified ? <CircularIndeterminate /> : <Inventory />}
        </PrivateRoute>

        <PrivateRoute path="/billing-reports">
          {isLoading || !isVerified ? <CircularIndeterminate /> : <BillingReports />}
        </PrivateRoute>

        <PrivateRoute path="/email-campaigns/create">
          {isLoading || !isVerified ? <CircularIndeterminate /> : <CreateEmailCampaign />}
        </PrivateRoute>

        <PrivateRoute path="/email-campaigns/edit/:id">
          {isLoading || !isVerified ? <CircularIndeterminate /> : <CreateEmailCampaign />}
        </PrivateRoute>

        <PrivateRoute path="/email-campaigns/unsubscribes">
          {isLoading || !isVerified ? <CircularIndeterminate /> : <ManageUnsubscribes />}
        </PrivateRoute>

        <PrivateRoute path="/email-campaigns/recipient-lists">
          {isLoading || !isVerified ? <CircularIndeterminate /> : <ManageRecipientLists />}
        </PrivateRoute>

        <PrivateRoute path="/email-campaigns/:id/details">
          {isLoading || !isVerified ? <CircularIndeterminate /> : <CampaignDetails />}
        </PrivateRoute>

        <PrivateRoute path="/email-campaigns">
          {isLoading || !isVerified ? <CircularIndeterminate /> : <EmailCampaigns />}
        </PrivateRoute>

        <PrivateRoute path="/data-pulse/create">
          {isLoading || !isVerified ? <CircularIndeterminate /> : <CreateSurvey />}
        </PrivateRoute>

        <PrivateRoute path="/data-pulse/view/:id">
          {isLoading || !isVerified ? <CircularIndeterminate /> : <ViewSurvey />}
        </PrivateRoute>

        <PrivateRoute path="/data-pulse/edit/:id">
          {isLoading || !isVerified ? <CircularIndeterminate /> : <CreateSurvey />}
        </PrivateRoute>

        <PrivateRoute path="/data-pulse/analytics/:id">
          {isLoading || !isVerified ? <CircularIndeterminate /> : <SurveyAnalytics />}
        </PrivateRoute>

        {/* Public survey route removed - surveys are now rendered by backend at services.pcsinconline.com/survey/:token */}

        <PrivateRoute path="/data-pulse">
          {isLoading || !isVerified ? <CircularIndeterminate /> : <DataPulse />}
        </PrivateRoute>

        <PrivateRoute path="/system-health">
          {isLoading || !isVerified ? <CircularIndeterminate /> : <SystemHealth />}
        </PrivateRoute>

        <PrivateRoute path="/maintenance/tasks">
          {isLoading || !isVerified ? <CircularIndeterminate /> : <MaintenanceTasks />}
        </PrivateRoute>

        <PrivateRoute path="/maintenance/jobs">
          {isLoading || !isVerified ? <CircularIndeterminate /> : <MaintenanceJobs />}
        </PrivateRoute>

        <PrivateRoute path="/maintenance/schedules">
          {isLoading || !isVerified ? <CircularIndeterminate /> : <MaintenanceSchedules />}
        </PrivateRoute>

        <PrivateRoute path="/maintenance/assets">
          {isLoading || !isVerified ? <CircularIndeterminate /> : <ManageAssets />}
        </PrivateRoute>

        {/* Fallback: redirect unknown paths to dashboard to avoid blank screens */}
        <Route render={() => <Redirect to="/dashboard" />} />
      </Switch>
    </Router>
  );
};

export default Routes;
